(function () {
  'use strict';

  var THROTTLE_MS = 120;
  var FLYOUT_CLOSE_DELAY = 250;
  var DESKTOP_BP = 1025;
  var TICKER_SPEED = 22;

  function isDesktop() {
    return window.innerWidth >= DESKTOP_BP;
  }

  /**
   * Header section wrapper — NOT always #shopify-section-header.
   * With {% sections 'header-group' %}, Shopify uses IDs like
   * shopify-section-template--123__header. Schema "class": "section-header"
   * is stable on that wrapper (dd-header + Dawn header both set it).
   */
  function getHeaderSection() {
    return (
      document.querySelector('.shopify-section.section-header') ||
      document.getElementById('shopify-section-header') ||
      document.querySelector('sticky-header.header-wrapper') ||
      null
    );
  }

  /* Skip header + announcement when sampling luminance under the nav */
  var HEADER_SKIP_SELECTOR =
    '.shopify-section.section-header, #shopify-section-header, ' +
    '#shopify-section-announcement-bar, [id$="__announcement-bar"]';

  /* ------------------------------------------------------------------
     Ticker — pulls announcement items into the glass panel
     ------------------------------------------------------------------ */

  function initTicker() {
    var section = document.getElementById('shopify-section-announcement-bar')
      || document.querySelector('.announcement-bar-section')
      || document.querySelector('[id*="announcement"].shopify-section');

    /* There are now TWO ticker slots in the DOM — the desktop one inside
       `.glass-nav-wrap` and a mobile-only one outside it (see
       sections/dd-header.liquid). Populate BOTH so the mobile nav and
       the condensed sticky bar both get the marquee. */
    var tickerEls = document.querySelectorAll('.glass-nav-announcement');
    if (!tickerEls.length) return;

    // innerHTML (not textContent) so inline wrappers travel with the
    // marquee clones. Matters for the `countdown` block, whose
    // `[data-dd-countdown]` span + `[data-dd-countdown-hours/minutes]`
    // slots need to stay intact inside every duplicated span so
    // assets/dd-countdown.js can keep ticking them after cloning.
    // Stock announcement blocks already render as `<span>...</span>`
    // inside the <p>, so this is a no-op for their visual output.
    var texts = [];
    var searchRoot = section || document;
    var items = searchRoot.querySelectorAll('.announcement-bar__message');
    for (var i = 0; i < items.length; i++) {
      var t = items[i].innerHTML.trim();
      if (t) texts.push(t);
    }

    if (section) {
      section.style.display = 'none';
    }

    if (texts.length === 0) {
      /* Fallback text is read from the first slot (desktop). Mobile slot
         is always empty on server render. */
      var fallback = tickerEls[0].textContent.trim();
      if (fallback) texts.push(fallback);
    }

    if (texts.length === 0) {
      /* No source announcements at all. Slots stay `:empty`; the rAF
         that fires `.glass-nav--ready` (later in init) also tags every
         `.glass-nav-announcement` with `.is-ready`, at which point the
         CSS `.is-ready:empty { display: none !important }` rule
         collapses the reserved min-height. Doing it there (instead of
         here, synchronously) means the reserved space lifts in the
         same paint as the rest of the glass-nav unsuppression — no
         intermediate downward shift on pages that have no announcement
         bar configured. */
      for (var h = 0; h < tickerEls.length; h++) tickerEls[h].style.display = 'none';
      return;
    }

    var sep = '\u2003\u2003\u00b7\u2003\u2003';
    var content = texts.join(sep);
    var chunk = content + sep;

    var repeats = Math.max(4, Math.ceil(3000 / chunk.length));
    var half = '';
    for (var r = 0; r < repeats; r++) half += chunk;

    var trackHtml =
      '<div class="glass-nav-ticker__track">' +
        '<span>' + half + '</span>' +
        '<span>' + half + '</span>' +
      '</div>';

    for (var k = 0; k < tickerEls.length; k++) {
      var tickerEl = tickerEls[k];
      tickerEl.innerHTML = trackHtml;
      /* NOTE: `.is-ready` is intentionally NOT added here. It lands in
         the same rAF as `.glass-nav--ready` on <html> (see init() near
         the bottom) so the `:not(.glass-nav--ready)` transition-
         suppression lifts at the exact same paint as the opacity flip
         from 0 → 0.75/0.8. If we added it here synchronously, a paint
         could land before the suppression lifts and the slot would
         pop instantly to its target opacity instead of fading. */

      /* Speed is read per-element from the nearest `.glass-nav-wrap`
         (desktop slot) or from `.dd-glass-header` for the mobile slot
         via closest() fallback. Keeps both in lockstep. */
      (function (el) {
        var wrap = el.closest('.glass-nav-wrap') || el.closest('.dd-glass-header');
        var speedSource = wrap && wrap.dataset && wrap.dataset.tickerSpeed;
        var speed = (speedSource && parseInt(speedSource, 10)) || TICKER_SPEED;
        requestAnimationFrame(function () {
          var track = el.querySelector('.glass-nav-ticker__track');
          if (!track) return;
          var halfWidth = track.scrollWidth / 2;
          var duration = halfWidth / speed;
          track.style.animationDuration = duration + 's';
        });
      })(tickerEl);
    }
  }

  /* ------------------------------------------------------------------
     Content Spacer
     ------------------------------------------------------------------ */

  function syncTotalHeaderHeight() {
    var headerSection = getHeaderSection();
    if (!headerSection) return;
    var bottom = headerSection.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--glass-header-total-h', Math.ceil(bottom) + 'px');
  }

  /* ------------------------------------------------------------------
     Content-Aware Brightness Detection
     ------------------------------------------------------------------ */

  function getLuminance(r, g, b) {
    var vals = [r, g, b].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * vals[0] + 0.7152 * vals[1] + 0.0722 * vals[2];
  }

  function parseBgColor(str) {
    if (!str || str === 'transparent' || str === 'rgba(0, 0, 0, 0)') return null;
    var m = str.match(/(\d+)/g);
    if (m && m.length >= 3) return { r: +m[0], g: +m[1], b: +m[2] };
    return null;
  }

  /* ------------------------------------------------------------------
     Image Brightness Cache

     Two-tier cache per image src:
       _imgLumCache[src]   → single whole-image average luminance
       _imgGridCache[src]  → 8x4 regional grid of luminances (32 cells),
                             row-major. Cells with insufficient alpha
                             coverage are -1.

     The whole-image average remains a fallback for races (image
     painted before its grid is ready) and for object-fit modes we
     don't try to map (fill / contain / none / scale-down). The grid
     is what makes sampling regional — without it every sample on the
     same <img> returns the same number, flattening full-bleed hero
     detection into one whole-image average.
     ------------------------------------------------------------------ */

  var GRID_COLS = 16;
  /* 16 rows (was 4): finer vertical resolution so a sample point at
     y = rect.bottom + 4 lands in a different cell than one at y = 0.
     The detector samples a band below the header to drive the
     gradient color; with 4 rows each cell was 25% of the image height,
     so "inside header" and "just below header" mapped to the same
     row and the band-below-header refactor produced identical values
     to the previous in-header sampling. 16 rows = ~6% per cell, which
     cleanly separates header zone from below-header zone on typical
     hero heights (700-900px). 16x16x3 = 768 bytes per image cache —
     trivial. */
  var GRID_ROWS = 16;
  var PROBE_WIDTH = 128; /* px served from the CDN for the probe — 8 source px per grid column */

  var _imgLumCache = {};
  var _imgGridCache = {};
  var _imgAvgRgbCache = {};
  var _lastPrescan = 0;
  var _detectHeader = null;

  function makeShopifyTiny(url) {
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('shopify.com') === -1) return url;
    var out = url.replace(/([?&])width=\d+/, '$1width=' + PROBE_WIDTH);
    if (out === url) out += (url.indexOf('?') !== -1 ? '&' : '?') + 'width=' + PROBE_WIDTH;
    return out;
  }

  function probeImage(key, url) {
    if (key in _imgLumCache) return;
    _imgLumCache[key] = -2;
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var c = document.createElement('canvas');
        c.width = GRID_COLS;
        c.height = GRID_ROWS;
        var ctx = c.getContext('2d');
        ctx.drawImage(probe, 0, 0, GRID_COLS, GRID_ROWS);
        var d = ctx.getImageData(0, 0, GRID_COLS, GRID_ROWS).data;
        var lums = new Float32Array(GRID_COLS * GRID_ROWS);
        var rgbs = new Uint8Array(GRID_COLS * GRID_ROWS * 3);
        var sum = 0, n = 0;
        var rSum = 0, gSum = 0, bSum = 0;
        for (var p = 0, idx = 0; p < d.length; p += 4, idx++) {
          if (d[p + 3] < 128) { lums[idx] = -1; continue; }
          var r = d[p], g = d[p + 1], b = d[p + 2];
          var lum = getLuminance(r, g, b);
          lums[idx] = lum;
          rgbs[idx * 3] = r;
          rgbs[idx * 3 + 1] = g;
          rgbs[idx * 3 + 2] = b;
          sum += lum;
          rSum += r;
          gSum += g;
          bSum += b;
          n++;
        }
        _imgLumCache[key] = n > 0 ? sum / n : -1;
        _imgAvgRgbCache[key] = n > 0 ? [Math.round(rSum / n), Math.round(gSum / n), Math.round(bSum / n)] : null;
        _imgGridCache[key] = { cols: GRID_COLS, rows: GRID_ROWS, lums: lums, rgbs: rgbs };
      } catch (e) { _imgLumCache[key] = -1; }
      if (_detectHeader) detectBrightness(_detectHeader);
    };
    probe.onerror = function () { _imgLumCache[key] = -1; };
    probe.src = url;
  }

  /* Map a viewport (x,y) onto the regional grid for a given <img>,
     returning the luminance and RGB of the cell underneath the point.

     Accounts for object-fit: cover (the dominant case for hero/full-
     bleed images) by computing the visible fraction of the natural
     image inside the layout box and re-mapping (u,v) into that range.
     For object-fit modes we don't attempt to map (fill / contain /
     none / scale-down), we return null so the caller can fall back to
     the whole-image average. */
  function getImgRegionData(img, x, y, key) {
    var grid = _imgGridCache[key];
    if (!grid) return null;
    var rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;

    var u = (x - rect.left) / rect.width;
    var v = (y - rect.top) / rect.height;
    if (u < 0) u = 0; else if (u > 1) u = 1;
    if (v < 0) v = 0; else if (v > 1) v = 1;

    var natW = img.naturalWidth || 0;
    var natH = img.naturalHeight || 0;
    if (natW > 0 && natH > 0) {
      var fit = '';
      try { fit = (window.getComputedStyle(img).objectFit || '').toLowerCase(); } catch (_e) {}
      var isCover = fit === '' || fit === 'cover';
      if (isCover) {
        var imgRatio = natW / natH;
        var boxRatio = rect.width / rect.height;
        if (imgRatio > boxRatio) {
          /* Image wider than box → cropped left+right */
          var visW = boxRatio / imgRatio;
          u = (1 - visW) / 2 + u * visW;
        } else if (imgRatio < boxRatio) {
          /* Image taller than box → cropped top+bottom */
          var visH = imgRatio / boxRatio;
          v = (1 - visH) / 2 + v * visH;
        }
      } else if (fit !== 'fill') {
        /* contain / none / scale-down — letterboxing makes (u,v) not
           map cleanly; bail to whole-image average. */
        return null;
      }
    }

    var col = Math.floor(u * grid.cols);
    if (col >= grid.cols) col = grid.cols - 1;
    var row = Math.floor(v * grid.rows);
    if (row >= grid.rows) row = grid.rows - 1;
    var idx = row * grid.cols + col;
    var lum = grid.lums[idx];
    if (lum >= 0) {
      return {
        lum: lum,
        rgb: [grid.rgbs[idx * 3], grid.rgbs[idx * 3 + 1], grid.rgbs[idx * 3 + 2]]
      };
    }
    return null;
  }

  function prescanImages() {
    var root = document.getElementById('MainContent') || document.body;
    var images = root.querySelectorAll('img');
    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      var realSrc = img.currentSrc || img.src || '';
      var dataSrc = img.dataset.src || '';
      var isPlaceholder = !realSrc || realSrc.indexOf('data:') === 0 || realSrc.indexOf('img_svg') !== -1;
      var declaredW = img.naturalWidth || parseInt(img.getAttribute('width')) || 0;

      if (!isPlaceholder && declaredW >= 100) {
        probeImage(realSrc, makeShopifyTiny(realSrc));
      } else if (dataSrc && dataSrc.indexOf('shopify.com') !== -1 && declaredW >= 100) {
        probeImage(dataSrc, makeShopifyTiny(dataSrc));
      }
    }
  }

  function samplePointColorAndLuminance(x, y, skipSelector) {
    try {
      var els = document.elementsFromPoint(x, y);
      for (var i = 0; i < els.length; i++) {
        if (els[i].closest(skipSelector)) continue;

        if (els[i].tagName === 'IMG') {
          var imgEl = els[i];
          var imgSrc = imgEl.currentSrc || imgEl.src;
          var dSrc = imgEl.dataset.src || '';
          /* Prefer regional grid sample — gives different luminance for
             different x-coordinates on the same image, so the nav-wide
             detector can read full-bleed heroes without collapsing to a
             whole-image average. */
          var gridKey =
            imgSrc && imgSrc in _imgGridCache ? imgSrc :
            dSrc && dSrc in _imgGridCache ? dSrc : null;
          if (gridKey) {
            var regional = getImgRegionData(imgEl, x, y, gridKey);
            if (regional) return regional;
          }
          /* Fallback: whole-image average while the grid is still
             warming up, or when object-fit isn't cover/fill. */
          var fallbackLum = -1;
          if (imgSrc && imgSrc in _imgLumCache && _imgLumCache[imgSrc] >= 0) {
            fallbackLum = _imgLumCache[imgSrc];
          } else if (dSrc && dSrc in _imgLumCache && _imgLumCache[dSrc] >= 0) {
            fallbackLum = _imgLumCache[dSrc];
          }
          if (fallbackLum >= 0) {
            var fallbackRgb = null;
            if (imgSrc && imgSrc in _imgAvgRgbCache) fallbackRgb = _imgAvgRgbCache[imgSrc];
            else if (dSrc && dSrc in _imgAvgRgbCache) fallbackRgb = _imgAvgRgbCache[dSrc];
            return { lum: fallbackLum, rgb: fallbackRgb };
          }
        }

        if (els[i].tagName === 'VIDEO') return { lum: 0.1, rgb: [0, 0, 0] };

        var bg = parseBgColor(getComputedStyle(els[i]).backgroundColor);
        if (bg) return { lum: getLuminance(bg.r, bg.g, bg.b), rgb: [bg.r, bg.g, bg.b] };
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  function clampPoint(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /* ------------------------------------------------------------------
     Logo Swap
     ------------------------------------------------------------------ */

  var LOGO_DARK_SRC = null;
  var LOGO_LIGHT_SRC = null;

  function initLogoPaths() {
    var logo = document.getElementById('glass-nav-logo');
    if (!logo) return;

    /* Prefer data attributes written by Liquid — supports image-picker logos */
    if (logo.dataset.logoLight) {
      LOGO_LIGHT_SRC = logo.dataset.logoLight;
      LOGO_DARK_SRC = logo.dataset.logoDark || logo.dataset.logoLight;
      return;
    }

    /* Fallback: derive from SVG asset filename convention (logo-light.svg / logo-dark.svg) */
    var src = logo.src || '';
    if (src.indexOf('logo-light') !== -1) {
      LOGO_LIGHT_SRC = src;
      LOGO_DARK_SRC = src.replace('logo-light', 'logo-dark');
    } else if (src.indexOf('logo-dark') !== -1) {
      LOGO_DARK_SRC = src;
      LOGO_LIGHT_SRC = src.replace('logo-dark', 'logo-light');
    }
  }

  /* Decide whether the logo should be locked to the dark variant regardless
     of what luminance detection last resolved.

     Two situations force dark:
       1. Solid nav style — the bar is always white everywhere, so the logo
          must always be dark. Luminance detection isn't even running in this
          mode, but we still guard here in case initLogoPaths() or a stale
          call path fires.
       2. Glass style, mobile, scrolled past SHOW_ZONE — the CSS paints the
          bar white via @media (max-width: 768px) + .glass-nav--condensed.
          The .glass-nav--condensed class toggles on every viewport, so we
          must also match the media query to avoid force-darking the logo on
          desktop where the bar is still glass. */
  /* Decide whether the logo should be locked to the dark variant regardless
     of what luminance detection last resolved.

     Forces dark when:
       1. Solid nav style — the bar is always white everywhere, so the logo
          must always be dark.
       2. Glass style + .glass-nav--condensed on <html> — past SHOW_ZONE we
          now morph to a full-width white sticky bar on every viewport, so
          the logo must flip to dark regardless of content underneath. The
          previous max-width:768px gate is gone because the condensed state
          paints white on desktop too. */
  function shouldForceDarkLogo() {
    if (document.documentElement.getAttribute('data-dd-nav-style') === 'solid') return true;
    if (document.documentElement.classList.contains('glass-nav--condensed')) return true;
    return false;
  }

  function setLogoSrc(isLightBg) {
    if (!LOGO_DARK_SRC) return;
    var logo = document.getElementById('glass-nav-logo');
    if (!logo) return;
    if (shouldForceDarkLogo()) isLightBg = true;
    logo.src = isLightBg ? LOGO_DARK_SRC : LOGO_LIGHT_SRC;
  }

  function swapLogo(isLightBg) {
    if (!LOGO_DARK_SRC) return;
    var logo = document.getElementById('glass-nav-logo');
    if (!logo) return;
    if (shouldForceDarkLogo()) isLightBg = true;
    logo.src = isLightBg ? LOGO_DARK_SRC : LOGO_LIGHT_SRC;
  }

  /* ------------------------------------------------------------------
     Theme Application
     ------------------------------------------------------------------ */

  var _currentTheme = null;

  function applyTheme(header, isLightBg, avgRgb) {
    var newTheme = isLightBg ? 'light' : 'dark';
    
    // We update color vars if the background changed from light to dark OR if avgRgb changed
    // Let's always update them because the RGB might change while scrolling over the same image
    var color = isLightBg ? '#222' : '#fff';
    var rgb = isLightBg ? '34,34,34' : '255,255,255';
    var bgRgb = isLightBg ? '255,255,255' : '34,34,34';
    var ctaText = isLightBg ? '#fff' : '#222';
    var ctaBg = isLightBg ? 'rgba(34,34,34,0.85)' : 'rgba(255,255,255,0.85)';

    var firstDetection = _currentTheme === null;
    _currentTheme = newTheme;

    /* Apply CSS variables immediately — nav text cross-fades via CSS transitions.
       The glass panel itself never hides; only text color and the logo change. */
    header.style.setProperty('--glass-nav-color', color);
    header.style.setProperty('--glass-nav-color-rgb', rgb);
    header.style.setProperty('--glass-nav-bg-rgb', bgRgb);
    header.style.setProperty('--glass-nav-cta-text', ctaText);
    header.style.setProperty('--glass-nav-cta-bg', ctaBg);
    
    if (avgRgb) {
      document.documentElement.style.setProperty('--glass-nav-gradient-rgb', avgRgb.join(', '));
      // Force Safari 26 to re-sample by recreating the notch element
      var oldNotch = document.getElementById('dd-ios-notch-top');
      if (oldNotch && oldNotch.dataset.rgb !== avgRgb.join(',')) {
        var newNotch = oldNotch.cloneNode();
        newNotch.dataset.rgb = avgRgb.join(',');
        oldNotch.parentNode.replaceChild(newNotch, oldNotch);
      }
    } else {
      document.documentElement.style.setProperty('--glass-nav-gradient-rgb', '0, 0, 0');
    }

    if (firstDetection) {
      setLogoSrc(isLightBg);
    } else {
      swapLogo(isLightBg);
    }
  }

  function detectBrightness(header) {
    /* Skip the entire content-aware color engine when the nav is in its
       condensed/sticky state. Two scenarios put the nav into condensed:
         1. User scrolled past SHOW_ZONE — the bar morphs into a solid
            white sticky bar with fixed dark text via the
            `.glass-nav--condensed` CSS rules.
         2. Mobile menu drawer is open — initDrawerCondensedSync() (defined
            in init() below) forces condensed so the expanded drawer reads
            against a solid white nav instead of the live caustic bar.
       In either case the bar's appearance is hard-coded by CSS, so
       sampling luminance under the bar is wasted work — five
       `elementsFromPoint` calls + canvas reads + getComputedStyle on
       every scroll-stop add up to multiple forced layouts per second
       on continuous scroll. */
    if (document.documentElement.classList.contains('glass-nav--condensed')) return;

    var now = Date.now();
    if (now - _lastPrescan > 3000) {
      _lastPrescan = now;
      prescanImages();
    }

    var rect = header.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    /* Sample a band JUST BELOW the header, not inside it.
       The gradient overlays the content under the header and fades to
       transparent at the bottom, so the perceived color blend the user
       sees is dominated by what's adjacent to where the gradient ends.

       Distribution: 9 points across two y-levels in the band (bandTop,
       bandBottom) at 9 evenly-spaced x positions. This avoids the
       previous 3-of-5-at-center-x clustering that biased the average
       toward whatever sat at the center-of-frame (e.g. a gold product
       pack against a dark background pulled the average gold). With
       9 horizontal points × 2 rows = 18 samples spread edge-to-edge,
       a center-only highlight gets diluted by the dark periphery. */
    var lPad = Math.min(80, rect.width * 0.2);
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var bandTop = rect.bottom + 4;
    var bandBottom = Math.min(rect.bottom + Math.max(80, rect.height), vh - 1);
    if (bandBottom <= bandTop) bandBottom = bandTop + 1;
    var H_SAMPLES = 9;
    var points = [];
    for (var hi = 0; hi < H_SAMPLES; hi++) {
      var fx = (hi + 0.5) / H_SAMPLES;
      var px = rect.left + lPad + (rect.width - 2 * lPad) * fx;
      points.push([px, bandTop]);
      points.push([px, bandBottom]);
    }

    var totalLum = 0;
    var rTotal = 0, gTotal = 0, bTotal = 0;
    var count = 0;
    var rgbCount = 0;
    var skipSel = HEADER_SKIP_SELECTOR;
    /* Track the dominant image src under the band so we can blend the
       regional average with the whole-image average — the regional
       sample alone over-reacts to local highlights (e.g. a centered
       product), but the whole-image average alone ignores what's
       actually adjacent to the gradient. A 50/50 blend gives the
       gradient a hue that's both image-aware AND representative of
       the overall composition. */
    var heroImgKey = null;
    for (var i = 0; i < points.length; i++) {
      var sx = points[i][0], sy = points[i][1];
      var sample = samplePointColorAndLuminance(sx, sy, skipSel);
      if (sample && sample.lum >= 0) {
        totalLum += sample.lum;
        if (sample.rgb) {
          rTotal += sample.rgb[0];
          gTotal += sample.rgb[1];
          bTotal += sample.rgb[2];
          rgbCount++;
        }
        count++;
      }
      if (!heroImgKey) {
        try {
          var els = document.elementsFromPoint(sx, sy);
          for (var ei = 0; ei < els.length; ei++) {
            if (els[ei].closest(skipSel)) continue;
            if (els[ei].tagName === 'IMG') {
              var imgSrc = els[ei].currentSrc || els[ei].src;
              var dSrc = els[ei].dataset.src || '';
              if (imgSrc && imgSrc in _imgAvgRgbCache) { heroImgKey = imgSrc; break; }
              if (dSrc && dSrc in _imgAvgRgbCache) { heroImgKey = dSrc; break; }
            }
          }
        } catch (_e) { /* ignore */ }
      }
    }
    if (count === 0) {
      applyTheme(header, false, null);
      return;
    }

    var avgLum = totalLum / count;
    var avgRgb = null;
    if (rgbCount > 0) {
      var rRegional = rTotal / rgbCount;
      var gRegional = gTotal / rgbCount;
      var bRegional = bTotal / rgbCount;
      var whole = heroImgKey ? _imgAvgRgbCache[heroImgKey] : null;
      if (whole) {
        /* 50% regional + 50% whole-image so the gradient color
           reflects both the area it overlays AND the dominant tone
           of the full composition. */
        avgRgb = [
          Math.round(rRegional * 0.5 + whole[0] * 0.5),
          Math.round(gRegional * 0.5 + whole[1] * 0.5),
          Math.round(bRegional * 0.5 + whole[2] * 0.5)
        ];
      } else {
        avgRgb = [Math.round(rRegional), Math.round(gRegional), Math.round(bRegional)];
      }
    }

    applyTheme(header, avgLum > 0.5, avgRgb);
  }

  /* ------------------------------------------------------------------
     Flyout
     ------------------------------------------------------------------ */

  function initFlyout() {
    var navItems = document.querySelectorAll(
      '.glass-nav-item[data-flyout-for], .glass-nav-utility-link[data-flyout-for]'
    );
    var flyoutContainer = document.querySelector('.glass-nav-flyout');
    if (!navItems.length || !flyoutContainer) return;

    var closeTimer = null;
    var exitCleanTimer = null;
    var isTouch = false;
    var touchOpenedFor = null;
    var activeItem = null;

    var LINK_EXIT_DURATION = 300; /* slightly more than glassLinkOut (250ms) */

    /* Switch to target panel + open grid.
       Keyframe animations fire automatically when is--active is added,
       so no reflow trick needed — works for both first-open and panel switch. */
    function openFlyout(handle) {
      clearTimeout(closeTimer);
      clearTimeout(exitCleanTimer);

      var panels = flyoutContainer.querySelectorAll('.glass-nav-flyout__panel');
      for (var i = 0; i < panels.length; i++) {
        var panel = panels[i];
        var isTarget = panel.getAttribute('data-flyout-for') === handle;
        /* Clear any in-progress exit on the panel we're reopening */
        panel.classList.remove('is--exiting');
        panel.classList.toggle('is--active', isTarget);
      }

      flyoutContainer.classList.add('is--open');
      activeItem = handle;
    }

    /* Start exit animation on active panel, collapse grid, clean up after anim */
    function performClose() {
      flyoutContainer.classList.remove('is--open');
      touchOpenedFor = null;
      activeItem = null;

      var panels = flyoutContainer.querySelectorAll('.glass-nav-flyout__panel');
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].classList.contains('is--active')) {
          panels[i].classList.add('is--exiting');
          panels[i].classList.remove('is--active');
        }
      }

      clearTimeout(exitCleanTimer);
      exitCleanTimer = setTimeout(function () {
        var exiting = flyoutContainer.querySelectorAll('.glass-nav-flyout__panel.is--exiting');
        for (var i = 0; i < exiting.length; i++) exiting[i].classList.remove('is--exiting');
      }, LINK_EXIT_DURATION);
    }

    function closeFlyout() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(performClose, FLYOUT_CLOSE_DELAY);
    }

    function cancelClose() {
      clearTimeout(closeTimer);
    }

    for (var i = 0; i < navItems.length; i++) {
      (function (item) {
        var handle = item.getAttribute('data-flyout-for');

        item.addEventListener('mouseenter', function () {
          if (isTouch) return;
          openFlyout(handle);
        });
        item.addEventListener('mouseleave', function () {
          if (isTouch) return;
          closeFlyout();
        });

        item.addEventListener('touchstart', function () { isTouch = true; }, { passive: true });

        item.addEventListener('click', function (e) {
          if (!isTouch) return;
          if (touchOpenedFor !== handle) {
            e.preventDefault();
            e.stopPropagation();
            openFlyout(handle);
            touchOpenedFor = handle;
          }
        });
      })(navItems[i]);
    }

    flyoutContainer.addEventListener('mouseenter', function () {
      if (isTouch) return;
      cancelClose();
    });
    flyoutContainer.addEventListener('mouseleave', function () {
      if (isTouch) return;
      closeFlyout();
    });

    flyoutContainer.addEventListener('click', function (e) {
      var rewardsLink = e.target.closest('[data-glass-rewards-link]');
      if (rewardsLink) {
        e.preventDefault();
        openSmilePanel();
        performClose();
        return;
      }
      var chatLink = e.target.closest('[data-dd-open-chat]');
      if (chatLink) {
        performClose();
      }
    });

    document.addEventListener('click', function (e) {
      if (!touchOpenedFor && !activeItem) return;
      if (
        !flyoutContainer.contains(e.target)
        && !e.target.closest('.glass-nav-item[data-flyout-for], .glass-nav-utility-link[data-flyout-for]')
      ) {
        performClose();
      }
    });
  }

  /* ------------------------------------------------------------------
     Rewards Button
     ------------------------------------------------------------------ */

  function initRewardsButton() {
    var iconsCell = document.querySelector('.glass-nav-cell--icons');
    if (!iconsCell) return;

    var btn = iconsCell.querySelector('.glass-nav-rewards-btn');
    if (!btn) {
      btn = document.createElement('button');
      btn.className = 'glass-nav-rewards-btn';
      btn.setAttribute('aria-label', 'Rewards');
      btn.type = 'button';
      btn.textContent = 'Rewards';
      /* Fallback order if Liquid button missing: Returns → Rewards → Login → Cart */
      var loginLink = iconsCell.querySelector('.glass-nav-utility-link--login');
      if (loginLink) iconsCell.insertBefore(btn, loginLink);
      else {
        var accountIcon = iconsCell.querySelector('.header__icon--account');
        if (accountIcon) iconsCell.insertBefore(btn, accountIcon);
        else {
          var cartIcon = iconsCell.querySelector('#cart-icon-bubble');
          if (cartIcon) iconsCell.insertBefore(btn, cartIcon);
          else iconsCell.appendChild(btn);
        }
      }
    }

    if (btn.dataset.ddSmileBound === '1') return;
    btn.dataset.ddSmileBound = '1';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      openSmilePanel();
    });
  }

  function openSmilePanel() {
    if (window.SmileUI && typeof SmileUI.openPanel === 'function') {
      SmileUI.openPanel();
      return;
    }
    var launcher = document.querySelector(
      'smile-ui-popup-launcher, .smile-launcher, .ssw-reward-tab-closed, [data-smile-launcher]'
    );
    if (launcher) {
      launcher.click();
      return;
    }
    var frame = document.querySelector('iframe[title*="Smile" i], iframe[src*="smile" i]');
    if (frame) {
      try { frame.contentDocument.querySelector('button, [role="button"]').click(); } catch (e) { /* cross-origin */ }
    }
  }

  /* ------------------------------------------------------------------
     Drawer Enhancements — sub-content, promo, delegation
     ------------------------------------------------------------------ */

  function initDrawerEnhancements() {
    var drawer = document.getElementById('menu-drawer');
    if (!drawer) return;

    var root = document.documentElement;
    var isLoggedIn = root.dataset.glassCustomer === 'true';
    var lastProductId = root.dataset.glassLastProductId || '';
    var fallbackProductId = root.dataset.glassFallbackProductId || '';

    /*
     * Persist the current PDP id so future non-PDP views (collections,
     * blog, about, cart) still have a personal seed for the drawer's
     * "Recommended for you" card. localStorage survives tab/session
     * reloads, so a customer who browsed a PDP an hour ago will keep
     * seeing it (or a related product) in the drawer promo.
     *
     * Wrapped in try/catch because Safari private mode disables
     * localStorage and throws on every write.
     */
    if (lastProductId) {
      try { localStorage.setItem('dd:lastViewedProduct', lastProductId); } catch (e) { /* no-op */ }
    }

    initDrawerDelegation(drawer);
    populatePromo(drawer, isLoggedIn, lastProductId, fallbackProductId);
  }

  function initDrawerDelegation(drawer) {
    drawer.addEventListener('click', function (e) {
      var closeBtn = e.target.closest('.glass-drawer-close button');
      if (closeBtn) {
        var summary = document.querySelector('#Details-menu-drawer-container > summary');
        if (summary) summary.click();
        return;
      }

      var backBtn = e.target.closest('.glass-drawer-back-btn');
      if (backBtn) {
        e.preventDefault();
        e.stopPropagation();
        hidePanel(drawer);
        return;
      }

      var rewardsLink = e.target.closest('a[href="#rewards"], [data-glass-rewards-link]');
      if (rewardsLink) {
        e.preventDefault();
        openSmilePanel();
        return;
      }

      var submenuBtn = e.target.closest('[data-submenu-for]');
      if (submenuBtn) {
        e.preventDefault();
        e.stopPropagation();
        var handle = submenuBtn.getAttribute('data-submenu-for');
        showPanel(drawer, handle);
        return;
      }
    }, true);

    var details = document.getElementById('Details-menu-drawer-container');
    if (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) resetPanels(drawer);
        
        // Swap logo when drawer opens/closes on glass nav pages
        if (document.documentElement.classList.contains('glass-nav-active')) {
          // When drawer is open, show dark logo (for white background)
          // When drawer is closed, show light logo (for glass/transparent background)
          setLogoSrc(details.open);
        }
      });
    }
  }

  function showPanel(drawer, handle) {
    var panel = drawer.querySelector('[data-submenu-panel="' + handle + '"]');
    if (!panel) return;

    var nav = drawer.querySelector('.menu-drawer__navigation');
    var menuList = drawer.querySelector('#glass-drawer-menu');
    var promo = drawer.querySelector('.glass-drawer-promo');
    var trigger = drawer.querySelector('[data-submenu-for="' + handle + '"]');
    var currentHandle = drawer.getAttribute('data-active-panel');
    var currentPanel = currentHandle
      ? drawer.querySelector('[data-submenu-panel="' + currentHandle + '"]')
      : null;

    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('data-active-panel', handle);

    if (currentPanel) {
      currentPanel.classList.remove('glass-drawer-panel--active');
      currentPanel.classList.add('glass-drawer--exiting');
    } else {
      menuList.classList.add('glass-drawer--exiting');
      if (promo) promo.classList.add('glass-drawer--exiting');
    }

    panel.removeAttribute('hidden');
    void panel.offsetHeight;
    panel.classList.add('glass-drawer-panel--active');

    if (currentPanel) {
      var exitingPanel = currentPanel;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!reduced || reduced.matches) {
        exitingPanel.setAttribute('hidden', '');
        exitingPanel.classList.remove('glass-drawer--exiting');
      } else {
        var onExit = function () {
          exitingPanel.removeEventListener('transitionend', onExit);
          exitingPanel.setAttribute('hidden', '');
          exitingPanel.classList.remove('glass-drawer--exiting');
        };
        exitingPanel.addEventListener('transitionend', onExit);
      }
    }

    if (nav) nav.scrollTop = 0;

    var backBtn = panel.querySelector('.glass-drawer-back-btn');
    if (backBtn) {
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!reduced || reduced.matches) {
        backBtn.focus({ preventScroll: true });
      } else {
        var onEnd = function () {
          panel.removeEventListener('transitionend', onEnd);
          backBtn.focus({ preventScroll: true });
        };
        panel.addEventListener('transitionend', onEnd);
      }
    }
  }

  function hidePanel(drawer) {
    var handle = drawer.getAttribute('data-active-panel');
    if (!handle) return;

    var panel = drawer.querySelector('[data-submenu-panel="' + handle + '"]');
    var parentHandle = panel ? panel.getAttribute('data-parent-panel') : null;
    var trigger = drawer.querySelector('[data-submenu-for="' + handle + '"]');
    var nav = drawer.querySelector('.menu-drawer__navigation');

    if (panel) panel.classList.remove('glass-drawer-panel--active');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    if (parentHandle) {
      var parentPanel = drawer.querySelector('[data-submenu-panel="' + parentHandle + '"]');
      if (parentPanel) {
        parentPanel.removeAttribute('hidden');
        parentPanel.classList.remove('glass-drawer--exiting');
        void parentPanel.offsetHeight;
        parentPanel.classList.add('glass-drawer-panel--active');
      }
      drawer.setAttribute('data-active-panel', parentHandle);
    } else {
      var menuList = drawer.querySelector('#glass-drawer-menu');
      var promo = drawer.querySelector('.glass-drawer-promo');
      if (menuList) menuList.classList.remove('glass-drawer--exiting');
      if (promo) promo.classList.remove('glass-drawer--exiting');
      drawer.removeAttribute('data-active-panel');
    }

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!reduced || reduced.matches) {
      if (panel) panel.setAttribute('hidden', '');
    } else if (panel) {
      var onEnd = function () {
        panel.removeEventListener('transitionend', onEnd);
        panel.setAttribute('hidden', '');
      };
      panel.addEventListener('transitionend', onEnd);
    }

    if (trigger) trigger.focus({ preventScroll: true });
    if (nav) nav.scrollTop = 0;
  }

  function resetPanels(drawer) {
    var allPanels = drawer.querySelectorAll('[data-submenu-panel]');
    var menuList = drawer.querySelector('#glass-drawer-menu');
    var promo = drawer.querySelector('.glass-drawer-promo');

    for (var i = 0; i < allPanels.length; i++) {
      allPanels[i].classList.remove('glass-drawer-panel--active', 'glass-drawer--exiting');
      allPanels[i].setAttribute('hidden', '');
    }

    var allTriggers = drawer.querySelectorAll('[data-submenu-for]');
    for (var j = 0; j < allTriggers.length; j++) {
      allTriggers[j].setAttribute('aria-expanded', 'false');
    }

    if (menuList) menuList.classList.remove('glass-drawer--exiting');
    if (promo) promo.classList.remove('glass-drawer--exiting');
    drawer.removeAttribute('data-active-panel');
  }

  /*
   * Products-collection constraint for `.glass-drawer-promo--reco`.
   *
   * The store's "Products" collection (admin title) is the built-in
   * Shopify all-products collection, which uses the special handle
   * `all`. The recommendation card must only ever show a product
   * that's a member of this collection — Shopify's
   * /recommendations/products.json doesn't accept a collection filter,
   * so we apply the constraint client-side: load the collection once
   * per page load, then intersect the recommendation API's results
   * against it. If the intersection is empty (or the recommendation
   * API returns nothing), we fall back to a random member of the
   * collection so the card still recommends something in-spec
   * rather than going neutral.
   *
   * The cache is a single Promise — concurrent reco renders during
   * the same session share it, so we make at most one
   * `/collections/all/products.json` request per page load.
   *
   * ──────────────────────────────────────────────────────────────
   * Why we additionally filter by the `exclude` tag
   * ──────────────────────────────────────────────────────────────
   * Shopify's `/collections/all/products.json` JSON endpoint has a
   * known quirk: it ignores any merchant-side customization of the
   * built-in `all` collection and returns the underlying full
   * product list. On this store, the admin-customized "Products"
   * collection reports `products_count: 23` from `/collections.json`,
   * but the JSON endpoint returns 28 — the 5-product delta is
   * sentinel/system products the merchant has manually removed from
   * the customer-facing collection (Free Shipping, InsureShield,
   * Gift Card, BOGO gifts, etc).
   *
   * Every one of those sentinels carries the `exclude` tag — the
   * merchant's standing convention for "do not show in customer-
   * facing surfaces". Mirroring that convention here gives us a
   * filter that's:
   *   • exactly aligned with how the merchant already curates the
   *     collection in admin (no new ground-truth to maintain),
   *   • forward-compatible — any new sentinel they add will be
   *     auto-excluded the moment they tag it,
   *   • robust to the JSON-endpoint quirk above without us having
   *     to spin up a duplicate manually-curated collection.
   * If the convention ever changes, swap RECO_EXCLUDE_TAGS below.
   */
  var PROMO_RECO_COLLECTION_HANDLE = 'all'; // Title: "Products"
  var RECO_EXCLUDE_TAGS = ['exclude', 'HIDE_IN_FABRIKATOR'];
  var cachedRecoCollection = null;

  function isRecoExcluded(product) {
    var tags = product && product.tags;
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i]).toLowerCase();
      for (var j = 0; j < RECO_EXCLUDE_TAGS.length; j++) {
        if (t === RECO_EXCLUDE_TAGS[j].toLowerCase()) return true;
      }
    }
    return false;
  }

  function loadRecoCollection() {
    if (cachedRecoCollection) return cachedRecoCollection;
    cachedRecoCollection = fetch(
      '/collections/' + PROMO_RECO_COLLECTION_HANDLE + '/products.json?limit=250'
    )
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var raw = (d && d.products) || [];
        // Strip sentinels at load time so every downstream consumer
        // (allowedIds set + random fallback pool) sees only valid
        // candidates. Doing the filter once here avoids re-checking
        // the tag list on every recommendation render.
        var clean = [];
        for (var i = 0; i < raw.length; i++) {
          if (!isRecoExcluded(raw[i])) clean.push(raw[i]);
        }
        return clean;
      })
      .catch(function () { return []; });
    return cachedRecoCollection;
  }

  function pickRandomFromCollection(products, excludeId) {
    if (!products || !products.length) return null;
    var pool = products;
    if (excludeId) {
      pool = [];
      for (var i = 0; i < products.length; i++) {
        if (String(products[i].id) !== String(excludeId)) pool.push(products[i]);
      }
    }
    if (!pool.length) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  function populatePromo(drawer, isLoggedIn, lastProductId, fallbackProductId) {
    var promo = drawer.querySelector('.glass-drawer-promo');
    if (!promo) return;

    // Guests always see the "Professionals Only" apply CTA — that's
    // the entire purpose of the card for that audience.
    if (!isLoggedIn) {
      buildProCallout(promo);
      return;
    }

    /*
     * Logged-in seed resolution (never falls back to the apply CTA).
     * Order of preference:
     *   1. Current PDP's product id (most relevant — they're looking
     *      at this exact product right now).
     *   2. Last-viewed PDP from localStorage (cached from a prior
     *      PDP visit; preserved across tabs and sessions).
     *   3. Server-provided fallback id (theme layout picks the first
     *      product from `most-popular` / `in-stock` / `all` — see
     *      theme.liquid).
     * If all three fail (brand-new store with zero products, which
     * realistically won't ship), we render a generic brand card
     * rather than the apply CTA — a logged-in pro seeing "Apply for
     * a pro account" is confusing and off-brand.
     */
    var seedId = lastProductId;
    if (!seedId) {
      try { seedId = localStorage.getItem('dd:lastViewedProduct') || ''; } catch (e) { seedId = ''; }
    }
    if (!seedId) seedId = fallbackProductId || '';

    promo.classList.add('glass-drawer-promo--reco');
    promo.innerHTML = '<div style="padding:20px;text-align:center;opacity:0.5;font-size:13px">Loading…</div>';

    loadRecoCollection().then(function (catalog) {
      if (!catalog.length) {
        // Either the fetch failed or the Products collection is
        // empty — neither should happen on a live store, but guard
        // anyway so we never hard-fail the card.
        buildBrandCard(promo);
        return;
      }

      // O(1) membership check during recommendation filtering.
      var allowedIds = {};
      for (var i = 0; i < catalog.length; i++) {
        allowedIds[String(catalog[i].id)] = true;
      }

      function pickInCollection(arr) {
        if (!arr) return null;
        for (var k = 0; k < arr.length; k++) {
          var id = String(arr[k].id);
          if (allowedIds[id] && id !== String(seedId)) return arr[k];
        }
        return null;
      }

      // No seed → there's no point hitting the recommendation API
      // (it requires `product_id`). Pick a random Products member.
      if (!seedId) {
        var direct = pickRandomFromCollection(catalog, '');
        if (direct) renderRecoCard(promo, direct);
        else buildBrandCard(promo);
        return;
      }

      // We ask for `limit=10` (instead of the previous limit=1) so
      // we have headroom to filter by collection membership and
      // still come away with at least one in-spec result for stores
      // where /collections/all is a strict subset of all merchant
      // products (e.g. some products are deliberately hidden from
      // it). limit=10 stays well under Shopify's 50-product cap.
      fetch('/recommendations/products.json?product_id=' + encodeURIComponent(seedId) + '&limit=10&intent=related')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var matched = pickInCollection(data && data.products);
          if (matched) { renderRecoCard(promo, matched); return; }

          /*
           * Either `intent=related` returned nothing, or it returned
           * products none of which are in the Products collection.
           * Retry once without `intent` (Shopify falls back to its
           * default mixed algorithm, which has wider coverage), then
           * if THAT still misses, render a random in-collection
           * product so the constraint always holds.
           */
          return fetch('/recommendations/products.json?product_id=' + encodeURIComponent(seedId) + '&limit=10')
            .then(function (r) { return r.json(); })
            .then(function (d2) {
              var matched2 = pickInCollection(d2 && d2.products);
              if (matched2) { renderRecoCard(promo, matched2); return; }
              var fallback = pickRandomFromCollection(catalog, seedId);
              if (fallback) renderRecoCard(promo, fallback);
              else buildBrandCard(promo);
            });
        })
        .catch(function () {
          // Recommendation API errored — degrade gracefully to a
          // random Products-collection member rather than the brand
          // card, since the constraint can still be satisfied.
          var fallback = pickRandomFromCollection(catalog, seedId);
          if (fallback) renderRecoCard(promo, fallback);
          else buildBrandCard(promo);
        });
    });
  }

  function renderRecoCard(el, p) {
    var img = p.featured_image || (p.images && p.images[0]) || '';
    var price = p.price != null ? ('$' + (p.price / 100).toFixed(2)) : '';
    el.className = 'glass-drawer-promo glass-drawer-promo--reco';
    el.innerHTML =
      '<a href="' + p.url + '" class="glass-drawer-promo__link">' +
        '<img src="' + img + '" alt="' + escapeHtml(p.title || '') + '" class="glass-drawer-promo__img" loading="lazy" width="400" height="160">' +
        '<div class="glass-drawer-promo__overlay">' +
          '<span class="glass-drawer-promo__label">Recommended for you</span>' +
          '<span class="glass-drawer-promo__title">' + escapeHtml(p.title || '') + '</span>' +
          (price ? '<span class="glass-drawer-promo__price">' + price + '</span>' : '') +
        '</div>' +
      '</a>';
  }

  function buildBrandCard(el) {
    /*
     * Logged-in zero-recommendation fallback. Neutral brand card
     * instead of the guest apply CTA — linking to /collections so
     * the customer still has somewhere to go from the drawer.
     */
    el.className = 'glass-drawer-promo glass-drawer-promo--brand';
    el.innerHTML =
      '<a href="/collections" class="glass-drawer-promo__link">' +
        '<div class="glass-drawer-promo__overlay">' +
          '<span class="glass-drawer-promo__label">Hair to die for</span>' +
          '<span class="glass-drawer-promo__title">Shop the full catalog</span>' +
        '</div>' +
      '</a>';
  }

  function buildProCallout(el) {
    /*
     * Apply CTA migrated to the dd-btn icon-solid sweep treatment
     * (clip-path wipe + marching arrow), matching the rest of the
     * site's primary CTAs (header login pill, syndicate access,
     * sticky footer apply, etc.). The structure mirrors what
     * snippets/dd-button.liquid renders for:
     *   { style: 'primary', size: 'md', icon_solid: true,
     *     icon: 'arrow-right' }
     * — kept inline here because this card is built by JS string
     * concatenation rather than Liquid, so we can't render the
     * snippet directly.
     *
     * The legacy `glass-drawer-promo__cta` class is preserved for
     * any layout/spacing rules that target it inside the promo
     * card; the visual cascade is handled by dd-btn (its
     * `glass-drawer-promo__cta` styling block in dd-glass-nav.css
     * is scoped with `:not(.btn)` so it only affects any future
     * non-btn variant).
     *
     * No `data-overlay-path` — falls through to the
     * dd-registration-overlay.js `/auth` default. The apply SPA at
     * apply.dropdeadextensions.com does not expose `/register`, so
     * pointing there would 404 inside the iframe. Matches the
     * canonical Apply CTAs across the rest of the theme.
     */
    el.className = 'glass-drawer-promo glass-drawer-promo--pro';
    el.innerHTML =
      '<div class="glass-drawer-promo__content">' +
        '<div class="glass-drawer-promo__badge">Professionals Only</div>' +
        '<p class="glass-drawer-promo__text">Register for a professional account to shop our exclusive products.</p>' +
        '<div class="glass-drawer-promo__actions">' +
          '<a class="btn btn--primary btn--medium btn--icon-solid glass-drawer-promo__cta" ' +
             'href="/account/register" ' +
             'data-registration-overlay-open>' +
            '<span class="btn__text">Apply</span>' +
            '<span class="btn__icon" aria-hidden="true">' +
              '<span class="btn__icon-text">Apply</span>' +
              '<span class="btn__icon-icon">' +
                '<svg class="icon icon--arrow-right" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
                  '<path d="M3 8h10m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>' +
              '</span>' +
            '</span>' +
          '</a>' +
          /* Login sibling. The apply SPA DOES expose a standalone
             `/login` pane (unlike `/register`), so we pin
             `data-overlay-path="/login"` to open it directly. The main
             `[data-registration-overlay-open]` delegate handles the
             click; `_bindLoginUrlInterceptor` explicitly skips anchors
             already wired to the overlay, so there is no double-open. */
          '<a class="btn btn--outline btn--medium glass-drawer-promo__login" ' +
             'href="/account/login" ' +
             'data-registration-overlay-open data-overlay-path="/login">' +
            '<span class="btn__text">Log in</span>' +
          '</a>' +
        '</div>' +
      '</div>';
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function bindCartDrawerFallback() {
    if (window._ddCartIconFallbackBound) return;
    window._ddCartIconFallbackBound = true;

    document.addEventListener('click', function (e) {
      var cartLink = e.target.closest('#cart-icon-bubble');
      if (!cartLink) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      var loginDrawer = document.querySelector('login-drawer.active');
      if (loginDrawer) {
        loginDrawer.classList.remove('active');
        document.body.classList.remove('login-drawer-open');
      }

      e.preventDefault();
      drawer.open(cartLink);
    }, true);

    document.addEventListener('keydown', function (e) {
      var cartLink = e.target.closest('#cart-icon-bubble');
      if (!cartLink) return;
      var code = (e.code || '').toUpperCase();
      if (code !== 'SPACE' && code !== 'ENTER') return;

      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      e.preventDefault();
      drawer.open(cartLink);
    });
  }

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */

  var _desktopInited = false;
  var _mobileInited = false;

  function init() {
    var isGlass = document.documentElement.classList.contains('glass-nav-active');
    /* isGlass = "bento nav is active on this page/template".
       isGlassStyle = "use the liquid-glass caustic visuals + content-aware
       color engine". Solid style reuses the exact same bento structure,
       scroll behavior, and drawer logic — it only skips the blur/caustic
       rendering and the luminance detection loop. */
    var isGlassStyle =
      document.documentElement.getAttribute('data-dd-nav-style') === 'glass';

    var headerSection = getHeaderSection();
    if (!headerSection) return;

    /* --- Shared setup (both glass and white nav) --- */

    function initForMode() {
      if (isDesktop()) {
        if (!_desktopInited) {
          initFlyout();
          _desktopInited = true;
        }
      } else {
        if (!_mobileInited) {
          initDrawerEnhancements();
          _mobileInited = true;
        }
      }
    }

    initForMode();
    bindCartDrawerFallback();
    syncTotalHeaderHeight();

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(syncTotalHeaderHeight).observe(headerSection);
    }

    window.addEventListener('resize', function () {
      syncTotalHeaderHeight();
      if (isGlass && isGlassStyle && document.elementsFromPoint) {
        detectBrightness(headerSection);
      }
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(initForMode, 200);
    });

    var _resizeTimer;

    /* ------------------------------------------------------------------
       Bottom-shelf wiring for iOS Safari 26 (Liquid Glass) — GLOBAL.
       Lives BEFORE the `if (!isGlass) return;` guard below because cart
       drawer + PDP sticky ATC exist on every template (PDP, search,
       collection-page, etc.), not just glass-nav templates (index +
       list-collections). Previous placement after the guard is why PDP
       saw no white shelf despite the wiring "looking right" in source.

       The helper, the cart-drawer observer, and the sticky-footer
       observer all run for every template. The mobile-menu wiring stays
       below the guard because it depends on glass-nav-only state
       (_condensed, applyCondensedState, SHOW_ZONE).
       ------------------------------------------------------------------ */
    /* Bottom-shelf tint registry. Map keyed by source name → color
       string ('white' | 'cream'). When any source is active, the
       most-recently-added color wins (insertion order via Map).
       Composing sources are typically same-color (cart-drawer +
       sticky-atc are both 'white'); the only cross-color case
       is the registration overlay (cream) which fully covers the
       viewport, so the only other plausible composer (sticky ATC)
       is hidden behind it anyway and its 'white' tint goes unused.

       The bottom notch carries `data-state="<color>|off"` so a
       color CHANGE — not just on/off — DOM-replaces and re-samples.
       Without that key, switching from white→cream while a source
       is still mounted would skip the replace and Safari would
       keep painting the previous color. */
    var _bottomShelfSources = new Map();
    function setBottomShelf(sourceName, color) {
      if (color) _bottomShelfSources.set(sourceName, color);
      else _bottomShelfSources.delete(sourceName);

      // Last-in-wins. Map preserves insertion order so iteration
      // ends on the most recently added entry.
      var resolved = null;
      _bottomShelfSources.forEach(function (c) { resolved = c; });

      var html = document.documentElement;
      html.classList.toggle('dd-drawer-bottom-white', resolved === 'white');
      html.classList.toggle('dd-drawer-bottom-cream', resolved === 'cream');

      var oldNotch = document.getElementById('dd-ios-notch-bottom');
      if (!oldNotch) return;
      var nextState = resolved || 'off';
      if (oldNotch.dataset.state === nextState) return;
      var newNotch = oldNotch.cloneNode();
      newNotch.dataset.state = nextState;
      oldNotch.parentNode.replaceChild(newNotch, oldNotch);
    }
    /* Backwards-compat shim. Existing call-sites (cart drawer,
       sticky ATC, mobile menu) keep using the boolean form. */
    function setBottomShelfWhite(sourceName, want) {
      setBottomShelf(sourceName, want ? 'white' : null);
    }

    /* Cart drawer (Dawn <cart-drawer>) — bottom-shelf wiring only.
       Unlike the mobile menu, the cart drawer is a side panel with a
       backdrop, not a fullscreen takeover; the top of the page is still
       scrollable behind it, so we leave the top notch alone (its
       scroll-driven dynamic/cream snap behavior is correct as-is) and
       only flip the bottom shelf to white while the drawer is open.

       Signal: <cart-drawer> toggles its own `active` class in open()
       (assets/cart-drawer.js:78) and close() (assets/cart-drawer.js:122).
       MutationObserver on that one attribute is the cheapest reliable
       hook — fires exactly once per open and once per close, no event
       wiring needed (Dawn dispatches `drawer:opened` but no
       `drawer:closed`, so observing the class is the only path that
       handles both transitions). */
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      var cartDrawerObserver = new MutationObserver(function () {
        setBottomShelfWhite('cart-drawer', cartDrawerEl.classList.contains('active'));
      });
      cartDrawerObserver.observe(cartDrawerEl, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    /* PDP sticky ATC (dd-sticky-footer) — also paints opaque pixels at
       the literal bottom of the viewport (`position: fixed; bottom: 0;
       z-index: 1000`), which competes with #dd-ios-notch-bottom for
       Safari 26's bottom-toolbar sampler. Even though the notch has
       higher z-index, Safari can still sample the footer's pixels
       depending on compositor layering — the symptom on PDP was the
       cart drawer opening but the bottom shelf staying tinted by the
       sticky ATC instead of going white.

       Fix: when sticky ATC is visible, force the bottom shelf white
       too. Visually correct (the bento card background is `#fff`,
       same as drawers), and the reference-counted Set in
       setBottomShelfWhite means cart-drawer + sticky-atc compose
       cleanly — closing the drawer while the sticky ATC is still
       on-screen keeps the shelf white.

       Signal: dd-sticky-footer.js already toggles `dd-sticky-footer-active`
       on <html> (assets/dd-sticky-footer.js:662 add, :680 remove). We
       observe <html>'s class attribute for that flag instead of
       editing the sticky-footer source. */
    var stickyFooterObserver = new MutationObserver(function () {
      setBottomShelfWhite(
        'sticky-atc',
        document.documentElement.classList.contains('dd-sticky-footer-active')
      );
    });
    stickyFooterObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    if (document.documentElement.classList.contains('dd-sticky-footer-active')) {
      setBottomShelfWhite('sticky-atc', true);
    }

    /* Mobile menu drawer — bottom-shelf wiring ONLY (global). Lives
       outside the `if (!isGlass) return;` guard because the mobile
       menu drawer ships on every template (PDP, collection page, etc.),
       not just glass-nav templates. The existing handler further down
       in init() (after the gate) coordinates condensed-state on
       glass-nav pages — that part stays gated because it depends on
       _condensed, applyCondensedState, SHOW_ZONE, etc. that are
       defined inside the glass-nav setup block.

       Two listeners on the same toggle event is fine: the helper is
       idempotent (Set dedupes 'mobile-menu' source name), so
       glass-nav pages will fire both listeners and the second
       setBottomShelfWhite call is a no-op via the dataset.state
       guard. */
    var menuDetailsForShelf = document.getElementById('Details-menu-drawer-container');
    if (menuDetailsForShelf) {
      menuDetailsForShelf.addEventListener('toggle', function () {
        setBottomShelfWhite('mobile-menu', menuDetailsForShelf.open);
      });
    }

    /* Registration iframe drawer (<registration-overlay>) — bottom-shelf
       wiring with the CREAM tint (drawer container is `var(--dd-bg)`
       = #fafaf9, painting white here would clash). Only fires on
       mobile widths where the drawer is bottom-anchored — the
       `@media (max-width: 767px)` rule in dd-registration-overlay.css
       transforms the container into a bottom sheet that touches the
       viewport bottom; on desktop the dialog is centered with a dark
       blurred backdrop in the bottom 48px gap, so cream-tinting the
       shelf there would look wrong (and the user wouldn't see the
       tint anyway because Safari 26's bottom toolbar is mobile-only).

       Signal: the FSM in dd-registration-overlay.js toggles `.active`
       on the <registration-overlay> element on open (line 653) and
       close (line 689). Same MutationObserver-on-class-attr pattern
       as the cart drawer above — fires once per open, once per close,
       and `setBottomShelf`'s data-state guard absorbs duplicates.

       On viewport flip (rotate / resize across the 767px threshold
       while the drawer is open) the matchMedia listener re-evaluates
       and either applies or clears the tint to match the new layout
       mode. */
    var regOverlayEl = document.querySelector('registration-overlay');
    if (regOverlayEl) {
      var regMqMobile = window.matchMedia('(max-width: 767px)');
      var updateRegOverlayShelf = function () {
        var isOpen = regOverlayEl.classList.contains('active');
        setBottomShelf(
          'registration-overlay',
          isOpen && regMqMobile.matches ? 'cream' : null
        );
      };
      var regOverlayObserver = new MutationObserver(updateRegOverlayShelf);
      regOverlayObserver.observe(regOverlayEl, {
        attributes: true,
        attributeFilter: ['class']
      });
      // Some older Safari builds only honour the legacy `addListener`
      // API on MediaQueryList; modern paths use `addEventListener`.
      // Try the new path first, fall back if it throws.
      if (regMqMobile.addEventListener) {
        regMqMobile.addEventListener('change', updateRegOverlayShelf);
      } else if (regMqMobile.addListener) {
        regMqMobile.addListener(updateRegOverlayShelf);
      }
      // Initialise in case the overlay is already open on script load
      // (rare — this script runs early — but cheap to cover).
      updateRegOverlayShelf();
    }

    if (!isGlass) return;

    /* --- Bento-nav setup (homepage + list-collections) — runs for both
           glass and solid styles so the ticker, logo paths, and scroll-
           driven condensed/hidden classes are wired up either way. --- */

    document.documentElement.classList.remove('glass-nav--ready');
    initLogoPaths();
    initTicker();

    _detectHeader = headerSection;
    /* prescanImages is the luminance-detection warm-up — only needed when
       the glass engine is going to run. */
    if (isGlassStyle) {
      prescanImages();
    } else {
      /* Solid style: no detection will ever run, so set the dark logo
         explicitly. On the homepage the Liquid template renders the LIGHT
         variant eagerly (for the dark hero), which would flash light-on-white
         until first paint of JS-driven theming. shouldForceDarkLogo() returns
         true here so the argument is ignored — always resolves to dark. */
      setLogoSrc(false);
    }

    var SHOW_ZONE = 80;
    var _ticking = false;
    var _condensed = false;

    /* Re-apply the logo for the current state. setLogoSrc already has the
       condensed override baked in, so this flips to dark when condensed and
       back to whatever luminance detection last resolved when un-condensed. */
    function reapplyLogo() {
      setLogoSrc(_currentTheme === 'light');
    }

    /* Scroll handler only toggles the condensed class now. The previous
       hide-on-scroll-down behavior (.glass-nav--hidden → translateY(-110%))
       has been removed — the nav stays on screen and morphs between the
       bento at top and the full-width white sticky bar past SHOW_ZONE.

       When the class flips, emit `dd:nav-condensed-change` on the window
       so downstream effects (hero parallax, etc.) can refresh any cached
       layout measurements that go stale because the header height just
       changed. Without this signal, scroll-tied animations read stale
       heroWrap.getBoundingClientRect() values for a frame and visibly
       bounce at the threshold crossing. */
    function notifyCondensedChange() {
      try {
        window.dispatchEvent(
          new CustomEvent('dd:nav-condensed-change', {
            detail: { condensed: _condensed }
          })
        );
      } catch (e) { /* IE-era CustomEvent constructor fallback not needed here */ }
    }

    /* ---- Condensed-state orchestration ----
     *
     * Desktop: instant class toggle. The CSS rules that morph the bento
     * pill into the full-width white bar (background / margin /
     * border-radius / box-shadow + backdrop-filter pseudo opacity) all
     * have a 0.25s transition, which looks great on hardware that can
     * keep up.
     *
     * Mobile (≤ 1024.98px): the same morph is the source of the "jerky"
     * scroll-to-condensed feel users reported. Animating margin and
     * border-radius forces a layout recalc on every frame, and the
     * .dd-glass-header::before backdrop-filter (blur 24px + saturate +
     * brightness) is recomputed across its own viewport-sized region as
     * its opacity fades — both of those are murderous on the mobile
     * compositor and can't keep 60fps on mid-tier Android in particular.
     *
     * Solution: on mobile we never let the user *see* the property
     * morph. Instead we do a transform-only slide-off / class-swap /
     * slide-in dance — GPU-cheap on every device:
     *
     *   1. Add `glass-nav--mobile-swap` → CSS slides the whole fixed
     *      section to translateY(-110%) (off the top of the viewport)
     *      using the transform transition that's already wired up at
     *      line ~845 of dd-glass-nav.css.
     *   2. After the slide-off completes, swap the
     *      `glass-nav--condensed` class while the bar is invisible. The
     *      multi-property morph still happens, but the user never sees
     *      it because the bar is off-screen — and we kill the underlying
     *      `transition: background, margin, …` declarations on mobile
     *      so even that off-screen recalc happens in a single frame.
     *   3. Remove `glass-nav--mobile-swap` → the section transitions
     *      back to translateY(0), revealing the new state with a clean
     *      slide-in.
     *
     * The orchestration is debounced via `_condensedTransitioning` so a
     * fast scroll past SHOW_ZONE and back doesn't pile animations on
     * top of each other; the most-recent desired state is held in
     * `_pendingCondensed` and applied once the in-flight swap settles.
     */
    var _condensedTransitioning = false;
    var _pendingCondensed = null;
    var SWAP_OUT_MS = 280;
    var SWAP_IN_MS = 280;

    function isMobileViewport() {
      return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 1024.98px)').matches
      );
    }

    function applyCondensedState(next) {
      document.documentElement.classList.toggle('glass-nav--condensed', next);
      _condensed = next;

      /* Safari 26 notch: snap to cream when entering condensed.
         detectBrightness() is gated by `.glass-nav--condensed` and
         skips itself in this state, so without this hook the notch
         retains whatever hero color was last sampled — and Safari
         26 ignores the CSS-var change alone (only element
         appearance/disappearance triggers a re-sample). DOM-replace
         here is the same mechanism applyTheme() uses on hero color
         change, just forced to cream. When leaving condensed, the
         next detectBrightness() pass fires automatically and
         DOM-replaces with the new hero color, so no explicit
         hook is needed for that direction.

         245, 245, 245 = #f5f5f5 = the cream that matches the sticky
         marquee. Keep this in sync with the CSS fallback in
         dd-glass-nav.css section 0d. */
      if (next) {
        document.documentElement.style.setProperty('--glass-nav-gradient-rgb', '245, 245, 245');
        var creamNotch = document.getElementById('dd-ios-notch-top');
        if (creamNotch && creamNotch.dataset.rgb !== '245,245,245') {
          var freshNotch = creamNotch.cloneNode();
          freshNotch.dataset.rgb = '245,245,245';
          creamNotch.parentNode.replaceChild(freshNotch, creamNotch);
        }
      }

      /* Strip the inline glass-theme custom properties when entering
         condensed. applyTheme() writes these as INLINE styles on the
         header element (`headerSection.style.setProperty('--glass-nav-color', ...)`)
         and inline custom properties always win over CSS rule values
         that lack !important — including the
         `.glass-nav-active.glass-nav--condensed .dd-glass-header { --glass-nav-color: #222 ... }`
         declarations a few hundred lines below in dd-glass-nav.css.
         Without this clear, a prior dark-hero detect leaves
         `--glass-nav-color: #fff` inline, and the condensed white bar
         renders white text on white background until the next
         scroll-stop redetects against post-hero (white) content.
         Now that detectBrightness is also gated by `.glass-nav--condensed`
         (above), that auto-correction never fires — so the strip is
         load-bearing, not just defensive. */
      if (next && headerSection) {
        headerSection.style.removeProperty('--glass-nav-color');
        headerSection.style.removeProperty('--glass-nav-color-rgb');
        headerSection.style.removeProperty('--glass-nav-bg-rgb');
        headerSection.style.removeProperty('--glass-nav-cta-text');
        headerSection.style.removeProperty('--glass-nav-cta-bg');
        /* Reset _currentTheme so the next applyTheme() (post-uncondense)
           is treated as a fresh detection and actually fires its
           setProperty pass — without this, _currentTheme === 'dark'
           causes applyTheme to early-return and the cleared inline
           values stay cleared even after we want them back. */
        _currentTheme = null;
      }

      reapplyLogo();
      notifyCondensedChange();
    }

    function setCondensed(next) {
      if (next === _condensed && !_condensedTransitioning) return;

      /* Desktop path: original behaviour preserved verbatim — the CSS
         transitions on .dd-glass-header own the smooth morph and look
         great on machines with a fast compositor. */
      if (!isMobileViewport()) {
        if (next === _condensed) return;
        applyCondensedState(next);
        return;
      }

      /* Mobile path: queue the desired end-state if we're mid-swap so
         the latest direction wins (e.g. user scrolls down past 80,
         flicks back up before the slide-in finishes). */
      if (_condensedTransitioning) {
        _pendingCondensed = next;
        return;
      }

      if (next === _condensed) return;

      _condensedTransitioning = true;

      /* Phase 1: slide whatever's currently visible off the top. */
      document.documentElement.classList.add('glass-nav--mobile-swap');

      setTimeout(function () {
        /* Phase 2 (off-screen, instant): swap visual state. The morph
           transition on .dd-glass-header is disabled on mobile, so all
           background / margin / border-radius / box-shadow changes land
           in a single frame while the bar is hidden above the viewport. */
        applyCondensedState(next);

        /* Phase 3: pull the section back into view. Removing the
           --mobile-swap class lets the existing transform transition
           ease translateY(-110%) → translateY(0). */
        document.documentElement.classList.remove('glass-nav--mobile-swap');

        setTimeout(function () {
          _condensedTransitioning = false;

          if (
            typeof _pendingCondensed === 'boolean' &&
            _pendingCondensed !== _condensed
          ) {
            var nextDesired = _pendingCondensed;
            _pendingCondensed = null;
            setCondensed(nextDesired);
          } else {
            _pendingCondensed = null;
          }
        }, SWAP_IN_MS);
      }, SWAP_OUT_MS);
    }

    window.addEventListener('scroll', function () {
      if (_ticking) return;
      _ticking = true;
      requestAnimationFrame(function () {
        var y = window.pageYOffset;
        setCondensed(y > SHOW_ZONE);
        _ticking = false;
      });
    }, { passive: true });

    /* ---- Mobile menu drawer ↔ condensed-state sync ----
     *
     * When the mobile menu drawer opens at top scroll the nav is still
     * in its bento/glass state — caustic blur + content-aware color
     * engine alive — and the expanded drawer reads on top of that
     * live, animated bar. The user wants the drawer-open state to
     * match scrolled-past-SHOW_ZONE behaviour: solid white sticky bar,
     * no caustics, no luminance sampling. The CSS rules at
     * `.glass-nav-active.glass-nav--condensed` already produce that
     * exact treatment for scroll, so we just force the same class on
     * mobile menu open and let the existing condensed cascade do the
     * work.
     *
     * We use applyCondensedState directly (NOT setCondensed) for two
     * reasons:
     *   1. setCondensed's mobile path runs a 280ms slide-off / swap /
     *      slide-in dance — that would compete visually with the
     *      drawer's own slide-in animation. Forcing condensed
     *      instantly avoids the double-animation collision.
     *   2. setCondensed is gated against re-entry while
     *      _condensedTransitioning is true. Calling applyCondensedState
     *      directly bypasses that gate so the menu-open force always
     *      lands in the same frame the drawer starts opening.
     *
     * On drawer close we recompute from current scroll position so a
     * user who had scrolled past SHOW_ZONE before opening the menu
     * stays condensed (correct), and a user who opened the menu at
     * top scroll drops back to the bento glass state (correct).
     *
     * The existing toggle handler in initDrawerDelegation() already
     * fires for the same details element to swap the logo — both
     * listeners run independently. Order doesn't matter:
     *   - existing-first → setLogoSrc(open) flips dark; then
     *     applyCondensedState(true) calls reapplyLogo() (idempotent).
     *   - new-first → applyCondensedState(true) sets _condensed=true,
     *     then setLogoSrc(open) sees shouldForceDarkLogo()===true and
     *     pins dark.
     */
    /* Bottom-shelf wiring (cart-drawer + sticky-atc + the
       setBottomShelfWhite helper itself) is set up earlier in init(),
       BEFORE the `if (!isGlass) return;` guard, so it runs on every
       template (PDP, search, collection page, etc.). Only the mobile-
       menu wiring below remains here because it depends on glass-nav-
       only state (_condensed, applyCondensedState, SHOW_ZONE). */
    var menuDetails = document.getElementById('Details-menu-drawer-container');
    if (menuDetails) {
      menuDetails.addEventListener('toggle', function () {
        /* Bottom-shelf wiring is handled by the global listener set up
           BEFORE the `if (!isGlass) return;` guard above. This
           handler is glass-nav-only and only coordinates condensed-
           state across menu open/close. */
        if (menuDetails.open) {
          if (!_condensed) applyCondensedState(true);
        } else {
          var shouldCondense = window.pageYOffset > SHOW_ZONE;
          if (shouldCondense !== _condensed) {
            applyCondensedState(shouldCondense);
            /* If we just dropped out of condensed back to the bento
               glass state, run detection immediately. Two reasons:
                 1. applyCondensedState(true) cleared the inline glass-
                    theme custom properties and reset _currentTheme to
                    null. The next scroll-stop would normally repopulate
                    them, but a user who closes the menu without
                    scrolling would see whatever the cleared state
                    produces (default `--glass-nav-color` from the CSS
                    fallbacks — usually dark on a dark hero == invisible).
                 2. detectBrightness has the new `glass-nav--condensed`
                    early-return guard, so before this point in the call
                    sequence (class removed first, then detect runs) the
                    engine was paused.
               Solid style pages skip this — applyTheme never runs in
               solid mode so there's no inline state to restore. */
            if (!shouldCondense && isGlassStyle && document.elementsFromPoint) {
              detectBrightness(headerSection);
            }
          }
        }
      });
    }

    /* Content-aware color engine — glass style only. Solid style has a
       white bar with fixed dark text, so there's nothing to detect and
       nothing to re-run on scroll/hero-flip.

       IMPORTANT: detectBrightness() calls `document.elementsFromPoint()`
       five times plus `getComputedStyle` and canvas sampling. Every one
       of those is a forced synchronous layout/style recalc. Previously
       this ran 120ms after every scroll pause, which in practice fired
       repeatedly during a continuous scroll whenever the user eased up
       for a single frame — every call yanked a frame away from the
       parallax animation.

       The nav colour only needs to resolve when scrolling STOPS (so the
       user can perceive the correct tint once they've landed on new
       content). We schedule it off the main scroll stream using
       requestIdleCallback where available, falling back to a longer
       timeout. Either way, it's cancelled and rescheduled on every
       scroll event, so continuous scrolling never triggers a detect. */
    if (isGlassStyle && document.elementsFromPoint) {
      detectBrightness(headerSection);

      var idleHandle = null;
      var scrollEndTimer = null;
      var SCROLL_END_MS = 180; /* ~3 frames of silence = scroll has stopped */

      function cancelDetect() {
        if (idleHandle !== null && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(idleHandle);
          idleHandle = null;
        }
        clearTimeout(scrollEndTimer);
      }

      function scheduleDetectOnIdle() {
        cancelDetect();
        scrollEndTimer = setTimeout(function () {
          if (typeof window.requestIdleCallback === 'function') {
            idleHandle = window.requestIdleCallback(function () {
              idleHandle = null;
              detectBrightness(headerSection);
            }, { timeout: 400 });
          } else {
            detectBrightness(headerSection);
          }
        }, SCROLL_END_MS);
      }

      window.addEventListener('scroll', scheduleDetectOnIdle, { passive: true });

      /* Scroll, init, resize, and image-probe completion are the only triggers.
         At the top of the page that means auto-advancing hero slides slip past
         without updating the nav — the content under the nav changes but no
         scroll event fires. Watch the hero's .is-active class for flips and
         re-trigger detectBrightness on every transition. */
      var detectTimer;
      function scheduleDetect() {
        clearTimeout(detectTimer);
        detectTimer = setTimeout(function () {
          detectBrightness(headerSection);
        }, THROTTLE_MS);
      }

      if (typeof MutationObserver !== 'undefined') {
        var hero = document.querySelector('[data-dd-hero]');
        if (hero) {
          var heroSlides = hero.querySelectorAll('.dd-hero__slide');
          if (heroSlides.length > 1) {
            var slideObserver = new MutationObserver(scheduleDetect);
            for (var s = 0; s < heroSlides.length; s++) {
              slideObserver.observe(heroSlides[s], {
                attributes: true,
                attributeFilter: ['class']
              });
            }
          }
        }
      }
    }

    requestAnimationFrame(function () {
      document.documentElement.classList.add('glass-nav--ready');
      /* Co-located with `.glass-nav--ready` so both class changes land
         in a single paint. At that paint:
           1. `:not(.glass-nav--ready)` no longer matches → the
              announcement slot's `transition: opacity 0.5s ease`
              becomes effective (the global `transition: none !important`
              suppression list lifts).
           2. `.is-ready:not(:empty)` matches → opacity rule jumps
              0 → 0.75 (desktop) / 0.8 (mobile).
         Browsers compute the post-change transition rule first, then
         resolve the property delta against it, so the opacity change
         transitions cleanly instead of snapping. Empty slots (no
         source announcements) also get `.is-ready` from initTicker's
         early-return branch and collapse via the `.is-ready:empty`
         display:none rule. */
      var ready = document.querySelectorAll('.glass-nav-announcement');
      for (var r = 0; r < ready.length; r++) {
        ready[r].classList.add('is-ready');
      }
    });

    /* Diagnostic surface — gated by `?debug=glass-nav` URL param OR by
       setting `window.__ddGlassNavDebug = true` before load. Exposes
       the internal caches and a forced re-sample call so I can read
       what the engine actually computed without a console wrapper.
       Safe to ship: zero overhead when the flag isn't set. */
    try {
      if (location.search.indexOf('debug=glass-nav') !== -1) {
        window.__ddGlassNavDebug = true;
      }
      if (window.__ddGlassNavDebug) {
        window.__ddGlassNav = {
          lumCache: _imgLumCache,
          gridCache: _imgGridCache,
          getCurrentTheme: function () { return _currentTheme; },
          forceDetect: function () { detectBrightness(headerSection); },
          GRID_COLS: GRID_COLS,
          GRID_ROWS: GRID_ROWS,
          THRESHOLD: 0.5,
          /* Pretty-print the grid for whichever IMG is currently
             "active" under the wrapper. Format: `row 0: 0.10 0.85 ...`
             one line per row, 16 cols. Lets me see whether the right-
             side cells (where Help/Community sit on screen) reflect
             the bright area you can see visually. */
          dumpGridForActiveSlide: function () {
            var slide = document.querySelector('.dd-hero__slide.is-active img') || document.querySelector('.dd-hero__media img');
            if (!slide) { console.log('no slide IMG found'); return; }
            var key = slide.currentSrc || slide.src;
            var grid = _imgGridCache[key];
            if (!grid) { console.log('no grid for', key); return; }
            console.log('Grid for', key.split('/').pop().split('?')[0]);
            console.log('  natural:', slide.naturalWidth, 'x', slide.naturalHeight, 'rect:', Math.round(slide.getBoundingClientRect().width) + 'x' + Math.round(slide.getBoundingClientRect().height));
            for (var r = 0; r < grid.rows; r++) {
              var row = [];
              for (var c = 0; c < grid.cols; c++) {
                var v = grid.lums[r * grid.cols + c];
                row.push(v >= 0 ? v.toFixed(2) : ' -- ');
              }
              console.log('  row ' + r + ':', row.join(' '));
            }
          }
        };
        console.log('[glass-nav] debug ready — call window.__ddGlassNav.forceDetect() to re-sample');
      }
    } catch (_e) { /* ignore */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

